import { addDaysYmd, formatDur } from "../../../../lib/dates";
import type { PaceBand, PlanSummary, PlanWeek, TrainingPlanLibrary } from "../../../../lib/trainingPlanApi";
import type { WizardRace } from "../../../../lib/raceSources";
import { useRovingRadio } from "../../../../hooks/useRovingRadio";
import { LucideIcon } from "../../../FG/LucideIcon";
import { ModalValHint } from "../../../modals/ModalValHint";
import { nearestPlan, type GoalMode } from "../finishTime";
import { formatStartInDays, formatYmdShort } from "../formatting";
import { PHASE_LEGEND, phaseColor } from "../phases";

/** Weekly mileage bars — lives inside each plan-length card on the Plan-length step.
 *  D-21: weeks before a mid-plan join render DIMMED (gray, no numeral);
 *  N-06: numerals only on alternating weeks + the race week. */
function PlanWeeklyMileageBars({
  weekly,
  isMobile,
  startWk
}: {
  weekly: PlanWeek[];
  isMobile: boolean;
  /** First week the athlete would actually run (1 = full plan). */
  startWk: number;
}) {
  const maxMiles = Math.max(...weekly.map((w) => w.weeklyMileage), 1);
  const barMax = isMobile ? 56 : 64;
  const lastWeek = weekly[weekly.length - 1]?.week ?? 0;

  return (
    <div className="fgp-wz-milechart">
      {weekly.map((w) => {
        const dimmed = w.week < startWk;
        const hasFlag = !!w.flag && !dimmed;
        const isRace = hasFlag && w.flag!.includes("Race");
        const showNumeral = !dimmed && (w.week % 2 === 1 || w.week === lastWeek);
        return (
          <div
            key={w.week}
            className="fgp-wz-milechart__col"
            title={`Week ${w.week} · ${w.phase} · ${Math.round(w.weeklyMileage)} mi · long ${w.longRunMiles} mi${w.flag ? ` · ${w.flag}` : ""}`}>
            <div className="fgp-wz-milechart__val">
              {showNumeral ? Math.round(w.weeklyMileage) : ""}
            </div>
            <div
              className="fgp-wz-milechart__bar"
              style={{
                height: Math.max(4, (w.weeklyMileage / maxMiles) * barMax),
                background: dimmed ? "var(--fg-gray-300)" : phaseColor(w.phase),
                opacity: dimmed ? 0.35 : undefined
              }}>
              {hasFlag && (
                <span className="fgp-wz-milechart__bar-ico" aria-hidden>
                  <LucideIcon
                    name={isRace ? "flag" : "chevron-up"}
                    size={10}
                    color="var(--fg-purple-deep)"
                    strokeWidth={2.5}
                  />
                </span>
              )}
            </div>
            <div className="fgp-wz-milechart__wk">{w.week}</div>
          </div>
        );
      })}
    </div>
  );
}

/** The wizard's PLAN-LENGTH step ("length") — DB lengths + phase bars + honest start-partway. */
export function PlanLengthStep({
  selectedRace,
  weeksOut,
  raceDate,
  library,
  sortedLengths,
  weeks,
  setWeeks,
  planFinish,
  fitWeeksOut,
  recommendedLen,
  today,
  isMobile,
  goalMode,
  plan,
  finish,
  goalLabel,
  band,
  jumpToGoal,
  nudge
}: {
  selectedRace: WizardRace | null;
  weeksOut: number;
  raceDate: string | null;
  library: TrainingPlanLibrary;
  sortedLengths: number[];
  weeks: number | null;
  setWeeks: (weeks: number) => void;
  planFinish: string | null;
  fitWeeksOut: number;
  recommendedLen: number | null;
  today: string;
  isMobile: boolean;
  goalMode: GoalMode;
  plan: PlanSummary | null;
  finish: string | null;
  /** D-21 context card line 2 — the goal recap ("4:00" / "Just finish"). */
  goalLabel: string | null;
  band: PaceBand | null;
  /** D-21 — the context card's "Edit goal" link jumps back to the Goal step. */
  jumpToGoal: () => void;
  /** D-15 — the Continue gate's validation nudge, rendered on-step. */
  nudge: string | null;
}) {
  const selectedLenIndex = sortedLengths.findIndex((w) => weeks === w);
  const rove = useRovingRadio({
    length: sortedLengths.length,
    selectedIndex: selectedLenIndex,
    onArrowSelect: (n) => {
      const w = sortedLengths[n];
      if (w != null) setWeeks(w);
    }
  });
  const shortestLen = sortedLengths[0];
  const longestLen = sortedLengths[sortedLengths.length - 1];
  return (
    <div className="fgp-wizard-panel">
      {/* D-21 — goal + race context card, so the length choice reads against
          the goal it serves. */}
      {selectedRace && (
        <div className="fgp-wizard-race-card" style={{ marginTop: 0 }}>
          <span
            className="fgo-event-opt__ico"
            style={{ background: "var(--fg-purple-50)" }}
            aria-hidden>
            <LucideIcon name="target" size={18} color="var(--fg-purple-deep)" strokeWidth={2} />
          </span>
          <span className="fgo-event-opt__body">
            <span className="fgo-event-opt__title">
              {[
                selectedRace.name,
                formatYmdShort(selectedRace.dateYmd),
                weeksOut > 0
                  ? `${weeksOut} ${weeksOut === 1 ? "week" : "weeks"} out`
                  : "less than a week away"
              ].join(" · ")}
            </span>
            <span className="fgo-event-opt__meta">
              Goal {goalLabel ?? "Any pace"}
              {band ? ` · race pace ${band.goalPace} · peak ${band.maxWeeklyMiles} mi/wk` : ""}
            </span>
          </span>
          <button
            type="button"
            className="fgp-wz-ramplink"
            style={{ flexShrink: 0, alignSelf: "center" }}
            onClick={jumpToGoal}>
            Edit goal
          </button>
        </div>
      )}
      <p className="fgp-wizard-race-hint" style={{ marginTop: 0 }}>
        {weeksOut > 0 ? (
          <>
            Race day is{" "}
            <strong style={{ color: "var(--fg-ink)" }}>
              {weeksOut} {weeksOut === 1 ? "week" : "weeks"} out
            </strong>
            . The charts below show your weekly mileage through to race day.
          </>
        ) : raceDate ? (
          <>
            Race day is{" "}
            <strong style={{ color: "var(--fg-ink)" }}>less than a week away</strong> — we&apos;ll
            fit as much as we can before then.
          </>
        ) : (
          <>The charts below show your weekly mileage through to race day.</>
        )}
      </p>

      <div
        className="fgp-wz-lenlist"
        role="radiogroup"
        aria-label="Plan length">
        {sortedLengths.map((w, lenI) => {
          const on = weeks === w;
          const p = nearestPlan(library.plans, w, planFinish ?? "");
          const startsMid = fitWeeksOut > 0 && fitWeeksOut < w;
          const startWk = startsMid ? w - fitWeeksOut + 1 : 1;
          const startDate = raceDate
            ? addDaysYmd(raceDate, -(Math.min(w, fitWeeksOut || w) * 7 - 1))
            : null;
          const longestRun = p
            ? Math.round(Math.max(...p.weekly.map((x) => x.longRunMiles)))
            : 0;
          // D-21 — one-line descriptor: shortest = sharper build, longest =
          // full periodized build; any other lengths go without one.
          const desc =
            w === longestLen
              ? "The full periodized build — base, build, peak, taper."
              : w === shortestLen
                ? "A shorter, sharper build — best if you're already running consistently."
                : null;
          return (
            <button
              key={w}
              type="button"
              role="radio"
              aria-checked={on}
              aria-pressed={on}
              tabIndex={rove.tabIndexFor(lenI)}
              onKeyDown={(e) => rove.onKeyDown(e, lenI)}
              onClick={() => setWeeks(w)}
              className={`fgp-wz-lencard${on ? " fgp-wz-lencard--on" : ""}`}>
              <div className="fgp-wz-lencard__head">
                <span className="fgp-wz-lencard__wk">{w} weeks</span>
                {w === recommendedLen && (
                  <span className="fgp-wz-lencard__badge">Recommended</span>
                )}
                <span className="fgp-wz-lencard__when">
                  {startsMid
                    ? `you'd join at week ${startWk}`
                    : startDate
                      ? `starts ${formatYmdShort(startDate)}${formatStartInDays(today, startDate)}`
                      : ""}
                </span>
              </div>
              {desc && <div className="fgp-wz-lencard__desc">{desc}</div>}
              {p && p.weekly.length > 0 && (
                <PlanWeeklyMileageBars weekly={p.weekly} isMobile={isMobile} startWk={startWk} />
              )}
              {p && (
                <div className="fgp-wz-lencard__stat">
                  {p.stats.runDaysPerWeek} sessions/wk · peaks at{" "}
                  <strong>{formatDur(p.stats.avgWeeklyMinutes)}/wk</strong>
                  {longestRun > 0 && (
                    <>
                      {" "}
                      · longest run <strong>{longestRun} mi</strong>
                    </>
                  )}
                </div>
              )}
              {startsMid && (
                <div className="fgp-wz-lencard__mid">
                  <LucideIcon name="flag" size={13} color="var(--fg-amber-ink)" strokeWidth={2} />
                  {weeksOut > 0
                    ? `Race is ${weeksOut} wks out`
                    : "Race is less than a week away"}{" "}
                  — you'd start at week {startWk}; the earlier weeks are skipped.
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="fgp-wizard-legend fgp-wz-planlegend">
        {PHASE_LEGEND.map((c) => (
          <span key={c.label} className="fgp-wizard-legend__item">
            <span className="fgp-wizard-legend__swatch" style={{ background: c.bar }} />
            {c.label}
          </span>
        ))}
      </div>

      {goalMode === "preset" && plan && plan.finishTime !== finish && (
        <div className="fgp-modal-callout fgp-modal-callout--purple">
          Showing the closest detailed plan ({plan.finishTime}); paces are tuned to your{" "}
          {finish} goal.
        </div>
      )}
      <ModalValHint message={nudge} />
    </div>
  );
}
